<template>
  <div>
    <list-table
      :records="records"
      :columns="tableColumns"
      :current-page-number.sync="currentPage"
      :total-records="totalRecords"
      :search-filter.sync="search"
      :show-search="false"
      :show-filter-by-default="true"
    >
      <template #filters>
        <b-row>
          <b-col cols="12" md="5" class="">
            <v-select
              class="filter-select"
              placeholder="Select Event Type"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :value="eventFilter"
              :options="eventOptions"
              :reduce="(val) => val.value"
              :disabled="loading"
              @input="(val) => (eventFilter = val)"
            />
          </b-col>
          <b-col cols="12" md="3" class="">
            <v-select
              class="filter-select"
              placeholder="Severity"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :value="eventSeverityFilter"
              :options="eventSeverityOptions"
              :reduce="(val) => val.value"
              :disabled="loading"
              @input="(val) => (eventSeverityFilter = val)"
            />
          </b-col>
        </b-row>
      </template>
      <template #cell(severity)="data">
        <b-badge
          pill
          :variant="`light-${resolveStatusVariant(data.item.severity)}`"
          class="text-capitalize"
        >
          {{ getUserStatusText(data.item.severity) }}
        </b-badge>
      </template>
      <template #cell(application)="data">
        <b-media vertical-align="middle">
          <template #aside>
            <b-avatar
              size="32"
              :src="getValueFromSource(data, 'item.application.logo', '')"
              :text="
                sentenceCase(
                  avatarText(
                    `${getValueFromSource(data, 'item.application.title', 'PAC')}`
                  )
                )
              "
              :variant="
                getValueFromSource(data, 'item.application.logo', '')
                  ? ''
                  : 'primary'
              "
              square
            />
          </template>
          <span>{{ getValueFromSource(data, "item.application.title", 'PAC') }}</span>
        </b-media>
      </template>
    </list-table>
  </div>
</template>

<script>
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import { debounce, get } from "lodash";
import { formatDistance } from "date-fns";
import {
  BLink,
  BButton,
  BModal,
  BRow,
  BCol,
  BTooltip,
  BAvatar,
  BMedia,
  BBadge,
  BDropdown,
  BDropdownItem,
} from "bootstrap-vue";

const watchHandler = {
  handler() {
    this.debounceFetchAllData();
  },
  immediate: false,
};

const watchHandlerRequiresPageReset = {
  handler() {
    this.currentPage = 1;
    this.debounceFetchAllData();
  },
  immediate: false,
};

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    vSelect,
    BAvatar,
    BTooltip,
    ListTable,
    BDropdown,
    BDropdownItem,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      records: [],

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      tableColumns: [
        { key: "application", label: "Application" },
        { key: "event" },
        { key: "description" },
        { key: "severity" },
        {
          key: "created",
          sortable: false,
          formatter: (val) => formatDistance(new Date(val), new Date(), { addSuffix: true }),
          label: "Date",
          class: "width-200",
        },
      ],
      eventSeverityOptions: [
        { label: "None", value: "none" },
        { label: "Low", value: "low" },
        { label: "Medium", value: "medium" },
        { label: "High", value: "high" },
        { label: "Critical", value: "critical" },
      ],
      eventSeverityFilter: "",
      eventFilter: "",
      eventOptions: [
        "Application Access Token Request",
        "Application Access OTP VerificationSuccess",
        "Application Access OTP VerificationFailed",
        "Application Login",
      ].map((item) => ({ label: item, value: item })),
      initialized: false
    };
  },
  computed: {
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value);
      },
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value);
      },
    },
    totalRecords: {
      get() {
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value) {
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value);
      },
    },
  },
  watch: {
    currentPage: watchHandler,
    eventFilter: watchHandler,
    eventSeverityFilter: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const {
      search,
      page = 1,
      limit = this.perPage,
      severity,
      event,
    } = this.$route.query;

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;

    if (severity) {
      this.eventSeverityFilter = severity;
    }

    if (event) {
      this.eventFilter = event;
    }

    this.$nextTick(() => { // hack-alert
      if (!this.initialized) {
        this.fetchAllData().then(() => { this.initialized = true });
      }
    });
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true;

        const query = {
          limit: this.perPage,
          // search: this.search,
          page: this.currentPage,
        };

        if (this.eventFilter) {
          query.event = this.eventFilter;
        }

        if (this.eventSeverityFilter) {
          query.severity = this.eventSeverityFilter;
        }

        await this.$router.push({ query }).catch(() => {});

        const request =
          await this.useJwt().mainService.users.applications.listSecurityLogs(
            query
          );
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        console.log("error_message", error_message);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    }
  },
};
</script>
